import * as React from 'react';
import { Box, Paper, type PaperProps } from '@mui/material';

/**
 * One dropdown-search behaviour for every picker, modelled on the Products search
 * (JobEstimatePartLineProductField): the list shows a short page of matches and ends with a
 * "Search more..." row that loads the next page. Row look (dividers, 13px text, hover colour, no
 * popup arrow) comes from the theme (`withOdooSearchDropdowns` in themes/themes.tsx), so every
 * MUI Autocomplete in the app shares it.
 *
 * Use:
 *   const more = useSearchMoreLimit(options, searchText);
 *   <SearchMoreProvider hasMore={more.hasMore} onMore={more.onMore}>
 *     <Autocomplete options={more.visible} slots={{ paper: SearchMorePaper }} ... />
 *   </SearchMoreProvider>
 */

/** Rows shown before "Search more..." — same as the Products search. */
export const SEARCH_PAGE_SIZE = 8;

type SearchMoreContextValue = { hasMore: boolean; onMore: () => void };
const SearchMoreContext = React.createContext<SearchMoreContextValue>({ hasMore: false, onMore: () => {} });

export function SearchMoreProvider({ hasMore, onMore, children }: SearchMoreContextValue & { children: React.ReactNode }) {
    const value = React.useMemo(() => ({ hasMore, onMore }), [hasMore, onMore]);
    return <SearchMoreContext.Provider value={value}>{children}</SearchMoreContext.Provider>;
}

/** Styling of the "Search more..." row — identical to the Products search. */
export const SEARCH_MORE_ROW_SX = {
    px: '12px',
    py: '7px',
    fontSize: 13,
    fontWeight: 500,
    color: 'primary.main',
    cursor: 'pointer',
    borderTop: '1px solid',
    borderColor: 'divider',
    userSelect: 'none',
    '&:hover': { bgcolor: (t: { palette: { mode: string } }) => (t.palette.mode === 'dark' ? 'rgba(144,202,249,0.08)' : '#e8f4fd') },
} as const;

/**
 * Autocomplete `slots.paper`: the option list, then "Search more..." when the provider says more
 * matches exist. mousedown is swallowed so the input keeps focus and the popup stays open.
 */
export const SearchMorePaper = React.forwardRef<HTMLDivElement, PaperProps>(function SearchMorePaper(
    { children, ...rest },
    ref
) {
    const { hasMore, onMore } = React.useContext(SearchMoreContext);
    return (
        <Paper ref={ref} {...rest}>
            {children}
            {hasMore ? (
                <Box
                    role="button"
                    data-search-more="true"
                    onMouseDown={e => {
                        e.preventDefault();
                        onMore();
                    }}
                    sx={SEARCH_MORE_ROW_SX}
                >
                    Search more...
                </Box>
            ) : null}
        </Paper>
    );
});

/**
 * Theme DEFAULT paper for every MUI Autocomplete that doesn't bring its own (themes/themes.tsx):
 * gives hand-built pickers the same behaviour without touching each one — only the first
 * SEARCH_PAGE_SIZE rows are shown, then "Search more..." reveals the next page. It stands aside
 * when the picker already pages itself (it renders its own "Search more..." row) and for grouped
 * lists. Arrowing past the visible rows reveals the next page too, so keyboard use never lands
 * on a hidden row. State lives in the paper, which mounts per open — every open starts at page 1.
 */
export const AutoLimitPaper = React.forwardRef<HTMLDivElement, PaperProps>(function AutoLimitPaper(
    { children, sx, ...rest },
    ref
) {
    const rootRef = React.useRef<HTMLDivElement | null>(null);
    const [limit, setLimit] = React.useState(SEARCH_PAGE_SIZE);
    const [count, setCount] = React.useState(0);

    const setRefs = React.useCallback(
        (el: HTMLDivElement | null) => {
            rootRef.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
        },
        [ref]
    );

    React.useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const update = () => {
            const options = Array.from(el.querySelectorAll<HTMLElement>('.MuiAutocomplete-option'));
            const pagesItself = options.some(o => /^search more/i.test((o.textContent ?? '').trim()));
            const grouped = el.querySelector('.MuiAutocomplete-groupLabel') != null;
            setCount(pagesItself || grouped ? 0 : options.length);
            const focused = options.findIndex(o => o.classList.contains('Mui-focused'));
            if (focused >= limit) setLimit(Math.ceil((focused + 1) / SEARCH_PAGE_SIZE) * SEARCH_PAGE_SIZE);
        };
        update();
        const observer = new MutationObserver(update);
        observer.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, [limit]);

    const limited = count > limit;
    return (
        <Paper
            ref={setRefs}
            {...rest}
            sx={[
                limited ? { [`& .MuiAutocomplete-option:nth-of-type(n+${limit + 1})`]: { display: 'none' } } : {},
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {children}
            {limited ? (
                <Box
                    role="button"
                    data-search-more="true"
                    onMouseDown={e => {
                        e.preventDefault();
                        setLimit(l => l + SEARCH_PAGE_SIZE);
                    }}
                    sx={SEARCH_MORE_ROW_SX}
                >
                    Search more...
                </Box>
            ) : null}
        </Paper>
    );
});

/**
 * Client-side page window over an already-loaded option list: first SEARCH_PAGE_SIZE rows, one
 * more page per "Search more...". Resets to the first page whenever `resetKey` (typically the
 * typed search text) changes.
 */
export function useSearchMoreLimit<T>(options: readonly T[], resetKey?: unknown) {
    const [pages, setPages] = React.useState(1);
    React.useEffect(() => setPages(1), [resetKey]);
    const limit = SEARCH_PAGE_SIZE * pages;
    const onMore = React.useCallback(() => setPages(p => p + 1), []);
    return {
        visible: options.length > limit ? options.slice(0, limit) : (options as T[]),
        hasMore: options.length > limit,
        onMore,
        limit,
    };
}
